import { prefersLessMotion } from '@utils/calendar/markUsedCelebration';
import { CALENDAR_SIDEBAR_TOGGLE_EASING, CALENDAR_SIDEBAR_TOGGLE_TRANSITION } from '@utils/calendar/calendarConstants';
import storage from '@utils/storage';

/** Marks the left sidebar, whose open width the switch's icons are worked out from. */
export const LEFT_SIDEBAR_ATTRIBUTE = 'data-left-sidebar';
/** Marks the sidebar's Calendar | Task List switch, not the narrow icon column's. */
export const SIDEBAR_VIEW_SWITCH_ATTRIBUTE = 'data-view-switch';
/** On the switches' icons. The sidebar's has none at its narrowest, where it shows the names only. */
export const VIEW_SWITCH_ICON_CLASS = 'view-switch-icon';

let flight: { ghosts: HTMLElement[]; animations: Animation[] } | null = null;

const stopFlight = () => {
  flight?.animations.forEach(animation => animation.cancel());
  flight?.ghosts.forEach(ghost => ghost.remove());
  flight = null;
};

/**
 * Where the switch's icons stand with the sidebar open, closing or opening alike. The switch itself can't say, the
 * sliding sidebar squeezing it, so a hidden copy is laid out at the open width and read.
 */
const openSwitchIconRects = (track: HTMLElement) => {
  const sidebar = track.closest<HTMLElement>(`[${LEFT_SIDEBAR_ATTRIBUTE}]`);
  if (!sidebar) return [];
  const side = parseFloat(getComputedStyle(track).marginLeft) || 0;
  const copy = track.cloneNode(true) as HTMLElement;
  Object.assign(copy.style, {
    position: 'absolute',
    left: `${side}px`,
    top: `${track.getBoundingClientRect().top - sidebar.getBoundingClientRect().top}px`,
    width: `${storage.getSidebarWidth() - side * 2}px`,
    margin: '0',
    visibility: 'hidden'
  });
  sidebar.appendChild(copy);
  const rects = Array.from(copy.querySelectorAll(`.${VIEW_SWITCH_ICON_CLASS}`)).map(icon => icon.getBoundingClientRect());
  copy.remove();
  return rects;
};

/**
 * Calendar and Task List travel between the sidebar's switch and the narrow icon column as the sidebar slides, so
 * closing it shows where they went (Petr, 2026-11-01). Copies of the icons do the flying, in the sidebar's own time, the
 * real ones held out of sight until they land; the switch fades as it goes. A student who asks for less motion, or a
 * sidebar too narrow for the switch's icons, gets no flight: the column's fade (ColumnViews) is enough.
 */
export const flyViewIcons = (pair: HTMLElement, intoColumn: boolean) => {
  // A second click mid-flight picks up from wherever the icons have got to.
  const midAir = flight?.ghosts.filter(ghost => ghost.isConnected).map(ghost => ghost.getBoundingClientRect());
  stopFlight();
  if (prefersLessMotion() || typeof pair.animate !== 'function') return;

  const track = document.querySelector<HTMLElement>(`[${SIDEBAR_VIEW_SWITCH_ATTRIBUTE}]`);
  if (!track) return;

  const animations = [
    track.animate(
      intoColumn
        ? [{ opacity: 1 }, { opacity: 0, offset: 0.4 }, { opacity: 0 }]
        : [{ opacity: 0 }, { opacity: 0, offset: 0.5 }, { opacity: 1 }],
      { duration: CALENDAR_SIDEBAR_TOGGLE_TRANSITION }
    )
  ];
  const ghosts: HTMLElement[] = [];
  flight = { ghosts, animations };

  const switchIcons = Array.from(track.querySelectorAll<HTMLElement>(`.${VIEW_SWITCH_ICON_CLASS}`));
  const columnIcons = Array.from(pair.querySelectorAll<HTMLElement>(`.${VIEW_SWITCH_ICON_CLASS}`));
  const switchRects = openSwitchIconRects(track);
  if (switchRects.length !== columnIcons.length) return;

  columnIcons.forEach((columnIcon, index) => {
    const switchIcon = switchIcons[index];
    const columnRect = columnIcon.getBoundingClientRect();
    const switchRect = switchRects[index];
    const place = ({ left, top, width }: { left: number; top: number; width: number }) =>
      `translate(${left}px, ${top}px) scale(${width / columnRect.width})`;
    const switchColor = getComputedStyle(switchIcon).color;
    const columnColor = getComputedStyle(columnIcon).color;

    const ghost = columnIcon.cloneNode(true) as HTMLElement;
    Object.assign(ghost.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      margin: '0',
      transformOrigin: '0 0',
      pointerEvents: 'none',
      zIndex: '10000',
      willChange: 'transform'
    });
    document.body.appendChild(ghost);
    ghosts.push(ghost);

    const start = midAir?.[index] ?? (intoColumn ? switchRect : columnRect);
    const end = intoColumn ? columnRect : switchRect;
    const flying = ghost.animate(
      [
        { transform: place(start), color: intoColumn ? switchColor : columnColor },
        { transform: place(end), color: intoColumn ? columnColor : switchColor }
      ],
      { duration: CALENDAR_SIDEBAR_TOGGLE_TRANSITION, easing: CALENDAR_SIDEBAR_TOGGLE_EASING, fill: 'both' }
    );
    flying.finished.then(
      () => ghost.remove(),
      () => ghost.remove()
    );
    animations.push(flying);

    [switchIcon, columnIcon].forEach(icon =>
      animations.push(icon.animate([{ opacity: 0 }, { opacity: 0 }], { duration: CALENDAR_SIDEBAR_TOGGLE_TRANSITION }))
    );
  });
};
